/**
 * Errors thrown by every trust-boundary primitive.
 *
 * Two distinct conditions:
 *
 *   - `BoundaryParseError` — the boundary received data but the shape did not
 *     match the declared schema. This means the source is lying about its
 *     contract (e.g. backend changed response shape, localStorage holds stale
 *     pre-migration JSON). Fix the source or the schema, never silently
 *     coerce.
 *
 *   - `BoundarySourceError` — the boundary could not fetch data at all
 *     (network down, storage quota exceeded, missing cookie). Distinct from
 *     parse errors so callers can choose to retry one but not the other.
 *
 * Both extend `BoundaryError` so a single `catch (e instanceof BoundaryError)`
 * covers anything coming out of the trust-boundary layer.
 */

export abstract class BoundaryError extends Error {
  readonly boundaryName: string

  constructor(boundaryName: string, message: string, options?: ErrorOptions) {
    super(message, options)
    this.name = this.constructor.name
    this.boundaryName = boundaryName
  }
}

export class BoundaryParseError extends BoundaryError {
  readonly issues: unknown
  readonly rawSample: string

  constructor(
    boundaryName: string,
    message: string,
    issues: unknown,
    raw: unknown,
  ) {
    super(boundaryName, message)
    this.issues = issues
    this.rawSample = sampleForLog(raw)
  }
}

export class BoundarySourceError extends BoundaryError {
  readonly cause?: unknown

  constructor(boundaryName: string, message: string, cause?: unknown) {
    super(boundaryName, message, cause instanceof Error ? { cause } : undefined)
    this.cause = cause
  }
}

/**
 * Stringify raw boundary input for error logs without dumping potentially
 * huge payloads. Caps at 500 chars and labels the type so a non-JSON response
 * (HTML error page, binary blob) is still recognisable in incident reports.
 */
function sampleForLog(value: unknown): string {
  if (typeof value === 'string') {
    return value.length > 500 ? `[string len=${value.length}] ${value.slice(0, 500)}…` : value
  }
  if (value === null || value === undefined) return String(value)
  try {
    const json = JSON.stringify(value)
    return json.length > 500 ? `[json len=${json.length}] ${json.slice(0, 500)}…` : json
  } catch {
    return `[unserialisable ${typeof value}]`
  }
}
